import logo from "assets/images/integrations/cleverreach_logo.png";
import { Map } from "immutable";
import { useCallback, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import { deleteIntegrationAction, saveIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { CleverReachSettingsForm } from "./SettingsForm";
import { CleverReachIntegrationSettings, CleverReachListsSettings } from "./types";

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  const integrationList = state
    .getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data",
      IntegrationType.CLEVERREACH
    ])
    ?.toJS() as CleverReachIntegrationSettings[] | undefined;

  const connectedIntegration = integrationList?.find((integration) => {
    return integration.oath2Config && integration.oath2Config.access_token;
  });

  const isDeleting = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "deletingItem",
        IntegrationType.CLEVERREACH,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  const isSaving = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "savingItem",
        IntegrationType.CLEVERREACH,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  return {
    selectedShop,
    connectedIntegration,
    isDeleting,
    isSaving
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      deleteIntegrationAction,
      saveIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type CleverReachSettingsViewComponentProps = ConnectedProps<typeof connector>;

const CleverReachSettingsViewComponent = (props: CleverReachSettingsViewComponentProps) => {
  const {
    connectedIntegration,
    isDeleting,
    selectedShop,
    deleteIntegrationAction,
    isSaving,
    saveIntegrationAction
  } = props;

  const handleDelete = useCallback(() => {
    if (connectedIntegration) {
      deleteIntegrationAction(selectedShop, IntegrationType.CLEVERREACH, connectedIntegration.id);
    }
  }, [selectedShop, connectedIntegration]);

  const handleSave = useCallback(
    (values: CleverReachListsSettings) => {
      const duplicateApp = { ...connectedIntegration };
      Object.assign(duplicateApp, { settings: values });

      saveIntegrationAction(selectedShop, IntegrationType.CLEVERREACH, duplicateApp);
    },
    [selectedShop, connectedIntegration]
  );

  const listOptions = useMemo(
    () => connectedIntegration?.info?.lists || [],
    [connectedIntegration?.info?.lists]
  );

  const initialValues = useMemo(
    () => Map(connectedIntegration?.settings || {}),
    [connectedIntegration?.settings]
  );

  if (!connectedIntegration) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={connectedIntegration.info?.accountName}
      deleting={isDeleting}
      image={logo}
      name="Cleverreach"
      onDelete={handleDelete}
    >
      <CleverReachSettingsForm
        listOptions={listOptions}
        initialValues={initialValues}
        saving={isSaving}
        handleSave={handleSave}
      />
    </ConnectedIntegrationLineWrapper>
  );
};

export const CleverReachSettingsView = connector(CleverReachSettingsViewComponent);
